import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';


const MemberDirectory = ({ tenantId, currentUserId, onSelectMember, onClose }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');

  useEffect(() => {
    loadMembers();
  }, [tenantId]);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('tenant_members')
        ?.select(`
          user_id,
          role,
          user_profiles(id, full_name, avatar_url, email)
        `)
        ?.eq('tenant_id', tenantId)
        ?.eq('is_active', true)
        ?.neq('user_id', currentUserId);

      if (error) throw error;

      const formattedMembers = data?.map(member => ({
        id: member?.user_profiles?.id,
        name: member?.user_profiles?.full_name,
        email: member?.user_profiles?.email,
        avatar: member?.user_profiles?.avatar_url,
        role: member?.role
      }));

      setMembers(formattedMembers || []);
    } catch (error) {
      console.error('Error loading members:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = members?.filter(member => {
    const matchesSearch = member?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         member?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesRole = filterRole === 'all' || member?.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'gym_owner': return 'bg-purple-100 text-purple-700';
      case 'trainer': return 'bg-blue-100 text-blue-700';
      case 'staff': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'gym_owner': return 'Owner';
      case 'trainer': return 'Trainer';
      case 'staff': return 'Staff';
      default: return 'Member';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-heading font-bold text-foreground">
              Start New Conversation
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-smooth"
              aria-label="Close"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon name="Search" size={18} color="var(--color-muted-foreground)" />
              </div>
              <input
                type="text"
                placeholder="Search members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
            </div>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e?.target?.value)}
              className="px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="all">All Roles</option>
              <option value="trainer">Trainers</option>
              <option value="staff">Staff</option>
              <option value="member">Members</option>
            </select>
          </div>
        </div>

        {/* Members List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Icon name="Loader2" size={32} className="animate-spin text-primary" />
            </div>
          ) : filteredMembers?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Icon name="Users" size={48} color="var(--color-muted-foreground)" className="mb-4" />
              <p className="text-muted-foreground">No members found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMembers?.map((member) => (
                <button
                  key={member?.id}
                  onClick={() => onSelectMember(member?.id)}
                  className="w-full p-4 flex items-center gap-3 hover:bg-muted rounded-lg transition-smooth text-left"
                >
                  {/* Avatar */}
                  {member?.avatar ? (
                    <AppImage
                      src={member?.avatar}
                      alt={member?.name || 'Member'}
                      className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Icon name="User" size={24} color="var(--color-primary)" />
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground truncate">
                      {member?.name || 'Unknown Member'}
                    </h3>
                    <p className="text-sm text-muted-foreground truncate">
                      {member?.email}
                    </p>
                  </div>

                  {/* Role Badge */}
                  <span className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                    getRoleBadgeColor(member?.role)
                  }`}>
                    {getRoleLabel(member?.role)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemberDirectory;